import { useCallback, useRef, useState } from "react";
import { Drawer } from "@design-system/Drawer";
import { CreateTemplateDetailsStep } from "./CreateTemplateDetailsStep";
import { CreateTemplateTasksStep } from "./CreateTemplateTasksStep";
import styles from "./CloseTemplateFormModal.module.scss";

type Step = "details" | "tasks";

interface Props {
    open: boolean;
    onClose: () => void;
}

export const CreateCloseTemplateDrawer = ({ open, onClose }: Props) => {
    const [step, setStep] = useState<Step>("details");
    const [createdTemplateId, setCreatedTemplateId] = useState<string | null>(
        null,
    );
    const [isTasksSaving, setIsTasksSaving] = useState(false);

    // Stable ref for the step-2 close-attempt guard.
    // Using a ref here is intentional: we need the outer Drawer's
    // onCloseAttempt to delegate to step 2's guard without causing
    // re-renders of this component when the guard updates.
    const innerCloseAttemptRef = useRef<((cancel: () => void) => void) | null>(
        null,
    );

    const handleAnimationEnd = useCallback((isOpen: boolean) => {
        if (!isOpen) {
            setStep("details");
            setCreatedTemplateId(null);
            innerCloseAttemptRef.current = null;
            setIsTasksSaving(false);
        }
    }, []);

    const handleCreated = useCallback((id: string) => {
        setCreatedTemplateId(id);
        setStep("tasks");
    }, []);

    const handleCloseAttempt = useCallback(
        (cancel: () => void) => {
            if (step === "tasks" && innerCloseAttemptRef.current) {
                innerCloseAttemptRef.current(cancel);
            }
        },
        [step],
    );

    const handleRegisterCloseAttempt = useCallback(
        (fn: (cancel: () => void) => void) => {
            innerCloseAttemptRef.current = fn;
        },
        [],
    );

    const handleSavingChange = useCallback((isSaving: boolean) => {
        setIsTasksSaving(isSaving);
    }, []);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            onCloseAttempt={handleCloseAttempt}
            onAnimationEnd={handleAnimationEnd}
            direction="right"
            size="fullScreen"
            noPadding
            dismissible={!isTasksSaving}
            headerAddonLeft="Create template"
        >
            <div className={styles.subtitleContainer}>
                Step {step === "details" ? "1" : "2"} of 3 ·{" "}
                {step === "details"
                    ? "Template details"
                    : "Add tasks and stages"}
            </div>
            {step === "details" && (
                <CreateTemplateDetailsStep onCreated={handleCreated} />
            )}
            {step === "tasks" && createdTemplateId && (
                <CreateTemplateTasksStep
                    closeTemplateId={createdTemplateId}
                    onClose={onClose}
                    onRegisterCloseAttempt={handleRegisterCloseAttempt}
                    onSavingChange={handleSavingChange}
                />
            )}
        </Drawer>
    );
};
